import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"; // Unused
import { User, Mail, Phone, MapPin, Building, FileText, Presentation, CreditCard } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../src/api";
import { toast } from "sonner";
import { isPaidStatus } from "@/lib/registration-utils";

interface RegistrationDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    registrationId: string | null;
}

interface RegistrationDetails {
    id: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string;
    correo: string;
    telefono?: string;
    pais: string;
    estado: string;
    ciudad: string;
    institucion: string;
    tipoParticipante: string;
    role: 'ATTENDEE' | 'SPEAKER';
    status: string;
    createdAt: string;
    // Solo ponentes
    tituloContribucion?: string | null;
    area?: string | null;
    resumen?: string | null;
    coautores?: any;
    paidAt?: string | null;
    syswebReference?: string | null;
    paymentAmount?: string | number | null;
    healthData?: {
        conditions?: string[];
        detailsImportant?: string;
        otherDetails?: string;
        emergencyContactName?: string;
        emergencyContactPhone?: string;
        bloodType?: string;
        medicalAuthorization?: boolean;
    } | null;
}

export function RegistrationDetailsModal({ isOpen, onClose, registrationId }: RegistrationDetailsModalProps) {
    const [data, setData] = useState<RegistrationDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen && registrationId) {
            const fetchData = async () => {
                setIsLoading(true);
                try {
                    const response = await api.get(`/registrations/${registrationId}`);
                    setData(response.data);
                } catch (error) {
                    console.error("Error fetching details", error);
                    toast.error("Error al cargar detalles");
                    onClose();
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        } else {
            setData(null);
        }
    }, [isOpen, registrationId, onClose]);

    if (!isOpen || !registrationId) return null;

    const isPonente = data?.role === 'SPEAKER';
    const hasHealthData = Boolean(data?.healthData?.conditions?.length);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-[90vw] md:max-w-lg rounded-xl shadow-2xl p-0 bg-white">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-600 via-cyan-500 to-green-500 rounded-t-xl shadow-lg" />

                <DialogHeader className="p-6 pb-4">
                    <div className="flex items-center space-x-3">
                        <DialogTitle className="font-semibold text-lg text-gray-900">
                            Detalles del Registro
                        </DialogTitle>
                        {isLoading ? (
                            <Badge variant="outline" className="animate-pulse">Cargando...</Badge>
                        ) : isPonente ? (
                            <Badge variant="outline" className="border-green-500 text-green-600 bg-green-50">
                                Ponente
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="border-blue-500 text-blue-600 bg-blue-50">
                                Asistente
                            </Badge>
                        )}
                    </div>
                    <DialogDescription>
                        {data ? `Información de ${data.nombre} ${data.apellidoPaterno}.` : 'Cargando información...'}
                    </DialogDescription>
                </DialogHeader>

                {isLoading || !data ? (
                    <div className="p-8 text-center text-gray-400">Cargando...</div>
                ) : (
                    <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                        <div className="space-y-4 divide-y divide-gray-100">
                            {/* Personal Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 pt-2">
                                <div className="flex items-start gap-3">
                                    <User className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Nombre Completo</p>
                                        <p className="font-semibold text-gray-800">{`${data.nombre} ${data.apellidoPaterno} ${data.apellidoMaterno}`}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Mail className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Correo Electrónico</p>
                                        <p className="font-semibold text-gray-800 break-all">{data.correo}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Phone className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Teléfono</p>
                                        <p className="font-semibold text-gray-800">{data.telefono || 'No proporcionado'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <MapPin className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Ubicación</p>
                                        <p className="font-semibold text-gray-800">{data.ciudad}, {data.pais}</p>
                                    </div>
                                </div>
                                <div className="col-span-1 md:col-span-2 flex items-start gap-3">
                                    <Building className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Institución</p>
                                        <p className="font-semibold text-gray-800">{data.institucion}</p>
                                    </div>
                                </div>

                                {/* Payment Info */}
                                {isPaidStatus(data.status) && (
                                    <div className="col-span-1 md:col-span-2 p-4 bg-green-50 border border-green-100 rounded-lg flex flex-col gap-3">
                                        <h3 className="text-sm font-bold text-green-800 flex items-center gap-2">
                                            <CreditCard className="h-4 w-4" /> Datos de Pago Conciliado
                                        </h3>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <p className="text-[10px] text-green-600 uppercase font-bold">Fecha</p>
                                                <p className="text-sm font-semibold text-green-900">{data.paidAt ? new Date(data.paidAt).toLocaleDateString() : 'Pendiente'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-green-600 uppercase font-bold">Concepto / Ref</p>
                                                <p className="text-sm font-semibold text-green-900 truncate" title={data.syswebReference || 'N/A'}>{data.syswebReference || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-green-600 uppercase font-bold">Monto</p>
                                                <p className="text-sm font-bold text-green-900">
                                                    ${data.paymentAmount ? Number(data.paymentAmount).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '0.00'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4">
                                <details className="rounded-md border border-rose-100 bg-rose-50/60 p-4">
                                    <summary className="cursor-pointer list-none font-semibold text-gray-900">
                                        Necesidades especiales o consideraciones de salud
                                    </summary>
                                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                                        {hasHealthData ? (
                                            <>
                                                <div>
                                                    <p className="text-xs font-bold uppercase text-rose-700">Condiciones registradas</p>
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {data.healthData?.conditions?.map((condition) => (
                                                            <Badge key={condition} variant="outline" className="border-rose-200 bg-white text-rose-700">
                                                                {condition}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                                {data.healthData?.detailsImportant && (
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Detalles importantes</p>
                                                        <p className="mt-1 whitespace-pre-wrap">{data.healthData.detailsImportant}</p>
                                                    </div>
                                                )}
                                                {data.healthData?.otherDetails && (
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Otra (especificar)</p>
                                                        <p className="mt-1 whitespace-pre-wrap">{data.healthData.otherDetails}</p>
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Contacto de emergencia</p>
                                                        <p className="mt-1">{data.healthData?.emergencyContactName || 'No registrado'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Teléfono de emergencia</p>
                                                        <p className="mt-1">{data.healthData?.emergencyContactPhone || 'No registrado'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Tipo de sangre</p>
                                                        <p className="mt-1">{data.healthData?.bloodType || 'No registrado'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold uppercase text-rose-700">Autorización médica</p>
                                                        <p className="mt-1">
                                                            {typeof data.healthData?.medicalAuthorization === 'boolean'
                                                                ? data.healthData.medicalAuthorization
                                                                    ? 'Sí autorizó asistencia médica'
                                                                    : 'No autorizó asistencia médica'
                                                                : 'No registrado'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-gray-600">Sin datos registrados previamente.</p>
                                        )}
                                    </div>
                                </details>
                            </div>

                            {/* Speaker Info */}
                            {isPonente && (
                                <div className="pt-4">
                                    <div className="rounded-md border p-4 bg-gray-50">
                                        <h3 className="text-md font-semibold mb-3 text-gray-900">Datos de la Ponencia</h3>
                                        <div className="space-y-3">
                                            <div className="flex items-start gap-3">
                                                <FileText className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm text-gray-500">Título</p>
                                                    <p className="font-medium text-gray-800">{data.tituloContribucion || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <Presentation className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm text-gray-500">Área</p>
                                                    <p className="font-medium text-gray-800">{data.area || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="p-6 pt-4 bg-gray-50 rounded-b-xl border-t">
                    <Button type="button" variant="outline" onClick={onClose}>Cerrar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
